import { InfoCircleOutlined } from '@ant-design/icons';
import {
  Form,
  type FormInstance,
  Input,
  Popover,
  Select,
  Typography,
} from 'antd';
import { isBoolean, isEmpty } from 'lodash';
import type React from 'react';
import {
  EXPLICIT_OR_IMPLICIT_OPTIONS,
  MIRACLE_MENTIONED_HOW_OPTIONS,
  RELIGIOUS_SLANG_FOUND,
  RT_TONE_OPTIONS,
  WHO_SAYS_IT_OPTIONS,
} from '../../../../../common/constants';
import CaseInsensitiveSelect, {
  toTitleCase,
} from '../../../../../components/CaseInsensitiveSelect';
import type { VoteEnum } from '../../../../../types/common.type';
import type { ReligiousTransparencyAnalysesData } from '../ReligiousTransparencyAnalysis';
import {
  characterAgePopoverContent,
  comparedReligionsPopoverContent,
  comparisonFrameDetectedPopoverContent,
  disrespectedReligionsPopoverContent,
  disrespectTowardsReligionPopoverContent,
  disrespectTriggerListPopoverContent,
  doctrineClaimTypePopoverContent,
  draftStanceByReligionPopoverContent,
  euphemismsListPopoverContent,
  framingStrengthPopoverContent,
  historicSlurListPopoverContent,
  historicSlursFoundPopoverContent,
  metaphoricReligiousTermFoundPopoverContent,
  metaphoricReligiousTermListPopoverContent,
  religiousSlangListPopoverContent,
  sexualRelationshipTypePopoverContent,
  suggestivePhrasesListPopoverContent,
  tonePopoverContent,
} from './BiasDetectedFieldsDetails';
import { firstPersonPopoverContent } from './SignalDetectionFieldsDetails';

const { Text } = Typography;

export type BiasDetectedFormValues = {
  vote?: VoteEnum;
  religiousClientComments?: string;
  religiousDraftStanceByReligion?: string[] | null | Record<string, string>;
  religiousReligionKeywordsFound?: boolean | null;
  religiousKeywordList?: string[] | null;
  religiousReligionsNamed?: string[] | null;
  religiousFirstOrSecondPersonPronouns?: boolean | null;
  religiousOverallBiasScore?: number | null;
  religiousReasonNote?: string | null;
  religiousMiracleMentionedHow?: string[] | null;
  religiousExplicitOrImplicit?: string[] | null;
  religiousWhoSaysIt?: string[] | null;
  religiousTone?: string[] | null;
  religiousToneConfidence?: number | null;
  religiousReligiousSlangFound?: boolean | null;
  religiousDownvote?: boolean | null;
  religiousUpvote?: boolean | null;
  religiousSlangList?: string[] | null;
  religiousHistoricSlursFound?: boolean | null;
  religiousHistoricSlurList?: string[] | null;
  religiousMetaphoricReligiousTermFound?: boolean | null;
  religiousMetaphoricReligiousTermList?: string[] | null;
  religiousDisrespectTriggerList?: string[] | null;
  religiousDisrespectedReligions?: string[] | null;
  religiousComparisonFrameDetected?: boolean | null;
  religiousComparedReligions?: string[] | null;
  religiousDoctrineClaimType?: string[] | null;
  religiousFramingStrength?: string[] | null;
  religiousDisrespectTowardsReligion?: boolean | null;
};

const infoIcon = (content: React.ReactNode) => (
  <Popover content={content} placement="top" overlayClassName="field-content">
    <InfoCircleOutlined className="signal-detection-info-icon" />
  </Popover>
);

const BiasDetectedForm: React.FC<{
  form: FormInstance;
  setIsSubmitBtnDisabled: React.Dispatch<React.SetStateAction<boolean>>;
  selectReligiousTransparencyData:
    | ReligiousTransparencyAnalysesData
    | undefined;
}> = ({ form, setIsSubmitBtnDisabled, selectReligiousTransparencyData }) => {
  const feedbackData = selectReligiousTransparencyData?.subSection?.feedback;

  return (
    <Form
      layout="vertical"
      form={form}
      className="signal-detection-form-grid"
      initialValues={{}}
      onValuesChange={() => setIsSubmitBtnDisabled(false)}
    >
      <div className="signal-detection-form-item signal-detection-form-item">
        <Form.Item
          label={
            <span className="d-flex align-center">
              Miracle Mentioned How{' '}
              {infoIcon(sexualRelationshipTypePopoverContent)}
            </span>
          }
          name="religiousMiracleMentionedHow"
          className={
            !isEmpty(feedbackData?.religiousMiracleMentionedHow)
              ? 'signal-detection-form-item--with-below-text'
              : 'signal-detection-form-item'
          }
        >
          <CaseInsensitiveSelect
            mode="tags"
            size="middle"
            options={MIRACLE_MENTIONED_HOW_OPTIONS}
          />
        </Form.Item>
        {!(
          JSON.stringify(feedbackData?.religiousMiracleMentionedHow) ===
          JSON.stringify(selectReligiousTransparencyData?.miracleMentionedHow)
        ) && (
          <div className="signal-detection-below-text">
            <span className="font-w-600">Originally:</span>{' '}
            <span>
              {(selectReligiousTransparencyData?.miracleMentionedHow || [])
                .map((val: string) => toTitleCase(val))
                .join(', ') || '--'}
            </span>
          </div>
        )}
      </div>
      <div className="signal-detection-form-item signal-detection-form-item">
        <Form.Item
          label={
            <span className="d-flex align-center">
              Explicit or Implicit {infoIcon(characterAgePopoverContent)}
            </span>
          }
          name="religiousExplicitOrImplicit"
          className={
            !isEmpty(feedbackData?.religiousExplicitOrImplicit)
              ? 'signal-detection-form-item--with-below-text'
              : 'signal-detection-form-item'
          }
        >
          <CaseInsensitiveSelect
            mode="tags"
            size="middle"
            options={EXPLICIT_OR_IMPLICIT_OPTIONS}
            placeholder="Select character age"
          />
        </Form.Item>
        {!(
          JSON.stringify(feedbackData?.religiousExplicitOrImplicit) ===
          JSON.stringify(selectReligiousTransparencyData?.explicitOrImplicit)
        ) && (
          <div className="signal-detection-below-text">
            <span className="font-w-600">Originally:</span>{' '}
            <span>
              {(selectReligiousTransparencyData?.explicitOrImplicit || [])
                .map((val: string) => toTitleCase(val))
                .join(', ') || '--'}
            </span>
          </div>
        )}
      </div>
      <div className="signal-detection-form-item signal-detection-form-item">
        <Form.Item
          label={
            <span className="d-flex align-center">
              Who Says It {infoIcon(euphemismsListPopoverContent)}
            </span>
          }
          name="religiousWhoSaysIt"
          className={
            !isEmpty(feedbackData?.religiousWhoSaysIt)
              ? 'signal-detection-form-item--with-below-text'
              : 'signal-detection-form-item'
          }
        >
          <CaseInsensitiveSelect
            mode="tags"
            size="middle"
            options={WHO_SAYS_IT_OPTIONS}
          />
        </Form.Item>
        {!(
          JSON.stringify(feedbackData?.religiousWhoSaysIt) ===
          JSON.stringify(selectReligiousTransparencyData?.whoSaysIt)
        ) && (
          <div className="signal-detection-below-text">
            <span className="font-w-600">Originally:</span>{' '}
            <span>
              {(selectReligiousTransparencyData?.whoSaysIt || [])
                .map((val: string) => toTitleCase(val))
                .join(', ') || '--'}
            </span>
          </div>
        )}
      </div>
      <div className="signal-detection-form-item signal-detection-form-item">
        <Form.Item
          label={
            <span className="d-flex align-center">
              Tone {infoIcon(tonePopoverContent)}
            </span>
          }
          name="religiousTone"
          className={
            !isEmpty(feedbackData?.religiousTone)
              ? 'signal-detection-form-item--with-below-text'
              : 'signal-detection-form-item'
          }
        >
          <CaseInsensitiveSelect
            mode="tags"
            size="middle"
            options={RT_TONE_OPTIONS}
          />
        </Form.Item>
        {!(
          JSON.stringify(feedbackData?.religiousTone) ===
          JSON.stringify(selectReligiousTransparencyData?.tone)
        ) && (
          <div className="signal-detection-below-text">
            <span className="font-w-600">Originally:</span>{' '}
            <span>
              {(selectReligiousTransparencyData?.tone || [])
                .map((val: string) => toTitleCase(val))
                .join(', ') || '--'}
            </span>
          </div>
        )}
      </div>
      <div className="signal-detection-form-item signal-detection-form-item">
        <Form.Item
          label={
            <span className="d-flex align-center">
              Tone Confidence{infoIcon(suggestivePhrasesListPopoverContent)}
            </span>
          }
          rules={[
            {
              min: 0,
              max: 1,
              message: 'Please enter a value between 0 and 1',
              type: 'number',
              transform(value) {
                return Number(value);
              },
            },
          ]}
          name="religiousToneConfidence"
          className={
            !isEmpty(feedbackData?.religiousToneConfidence)
              ? 'signal-detection-form-item--with-below-text'
              : 'signal-detection-form-item'
          }
          normalize={(value) => {
            if (value === '') return '';
            const num = parseFloat(value);
            if (isNaN(num)) return '';

            const clamped = Math.min(Math.max(0, num), 2);
            return Number(clamped.toFixed(2));
          }}
        >
          <Input type="number" min={0} max={1} step={0.1} />
        </Form.Item>
        {feedbackData?.religiousToneConfidence &&
          selectReligiousTransparencyData?.toneConfidence &&
          feedbackData?.religiousToneConfidence !==
            selectReligiousTransparencyData?.toneConfidence && (
            <div className="signal-detection-below-text">
              <span className="font-w-600">Originally:</span>{' '}
              <span>
                {selectReligiousTransparencyData?.toneConfidence?.toFixed(2)}
              </span>
            </div>
          )}
      </div>
      <div className="signal-detection-form-item signal-detection-form-item">
        <Form.Item
          label={
            <span className="d-flex align-center">
              Religious Slang Found {infoIcon(firstPersonPopoverContent)}
            </span>
          }
          name="religiousReligiousSlangFound"
          className={
            isBoolean(feedbackData?.religiousReligiousSlangFound)
              ? 'signal-detection-form-item--with-below-text'
              : 'signal-detection-form-item'
          }
        >
          <Select size="middle" options={RELIGIOUS_SLANG_FOUND} />
        </Form.Item>

        {isBoolean(feedbackData?.religiousReligiousSlangFound) &&
          isBoolean(selectReligiousTransparencyData?.religiousSlangFound) &&
          !(
            feedbackData?.religiousReligiousSlangFound ===
            selectReligiousTransparencyData?.religiousSlangFound
          ) && (
            <div className="signal-detection-below-text">
              <Text strong>Originally:</Text>{' '}
              <Text>
                {selectReligiousTransparencyData?.religiousSlangFound === true
                  ? 'True'
                  : selectReligiousTransparencyData?.religiousSlangFound ===
                      false
                    ? 'False'
                    : ''}
              </Text>
            </div>
          )}
      </div>
      <div className="signal-detection-form-item signal-detection-form-item">
        <Form.Item
          label={
            <span className="d-flex align-center">
              Religious Slang List {infoIcon(religiousSlangListPopoverContent)}
            </span>
          }
          name="religiousReligiousSlangList"
          className={
            !isEmpty(feedbackData?.religiousReligiousSlangList)
              ? 'signal-detection-form-item--with-below-text'
              : 'signal-detection-form-item'
          }
        >
          <CaseInsensitiveSelect
            mode="tags"
            size="middle"
            options={[]}
            placeholder="Enter slang terms"
          />
        </Form.Item>
        {!(
          JSON.stringify(feedbackData?.religiousReligiousSlangList) ===
          JSON.stringify(selectReligiousTransparencyData?.religiousSlangList)
        ) && (
          <div className="signal-detection-below-text">
            <span className="font-w-600">Originally:</span>{' '}
            <span>
              {(selectReligiousTransparencyData?.religiousSlangList || [])
                .map((val: string) => toTitleCase(val))
                .join(', ') || '--'}
            </span>
          </div>
        )}
      </div>
      <div className="signal-detection-form-item signal-detection-form-item">
        <Form.Item
          label={
            <span className="d-flex align-center">
              Historic Slurs Found {infoIcon(historicSlursFoundPopoverContent)}
            </span>
          }
          name="religiousHistoricSlursFound"
          className={
            isBoolean(feedbackData?.religiousHistoricSlursFound)
              ? 'signal-detection-form-item--with-below-text'
              : 'signal-detection-form-item'
          }
        >
          <Select
            size="middle"
            options={[
              { label: 'True', value: true },
              { label: 'False', value: false },
            ]}
          />
        </Form.Item>
        {isBoolean(feedbackData?.religiousHistoricSlursFound) &&
          isBoolean(selectReligiousTransparencyData?.historicSlursFound) &&
          feedbackData?.religiousHistoricSlursFound !==
            selectReligiousTransparencyData?.historicSlursFound && (
            <div className="signal-detection-below-text">
              <Text strong>Originally:</Text>{' '}
              <Text>
                {selectReligiousTransparencyData?.historicSlursFound === true
                  ? 'True'
                  : selectReligiousTransparencyData?.historicSlursFound ===
                      false
                    ? 'False'
                    : ''}
              </Text>
            </div>
          )}
      </div>
      <div className="signal-detection-form-item signal-detection-form-item">
        <Form.Item
          label={
            <span className="d-flex align-center">
              Historic Slur List {infoIcon(historicSlurListPopoverContent)}
            </span>
          }
          name="religiousHistoricSlurList"
          className={
            !isEmpty(feedbackData?.religiousHistoricSlurList)
              ? 'signal-detection-form-item--with-below-text'
              : 'signal-detection-form-item'
          }
        >
          <CaseInsensitiveSelect
            mode="tags"
            size="middle"
            options={[]}
            placeholder="Enter historic slurs"
          />
        </Form.Item>
        {!(
          JSON.stringify(feedbackData?.religiousHistoricSlurList) ===
          JSON.stringify(selectReligiousTransparencyData?.historicSlurList)
        ) && (
          <div className="signal-detection-below-text">
            <span className="font-w-600">Originally:</span>{' '}
            <span>
              {(selectReligiousTransparencyData?.historicSlurList || [])
                .map((val: string) => toTitleCase(val))
                .join(', ') || '--'}
            </span>
          </div>
        )}
      </div>

      <div className="signal-detection-form-item signal-detection-form-item">
        <Form.Item
          label={
            <span className="d-flex align-center">
              Metaphoric Religious Term Found{' '}
              {infoIcon(metaphoricReligiousTermFoundPopoverContent)}
            </span>
          }
          name="religiousMetaphoricReligiousTermFound"
          className={
            isBoolean(feedbackData?.religiousMetaphoricReligiousTermFound)
              ? 'signal-detection-form-item--with-below-text'
              : 'signal-detection-form-item'
          }
        >
          <Select
            size="middle"
            options={[
              { label: 'True', value: true },
              { label: 'False', value: false },
            ]}
          />
        </Form.Item>
        {isBoolean(feedbackData?.religiousMetaphoricReligiousTermFound) &&
          isBoolean(
            selectReligiousTransparencyData?.metaphoricReligiousTermFound,
          ) &&
          feedbackData?.religiousMetaphoricReligiousTermFound !==
            selectReligiousTransparencyData?.metaphoricReligiousTermFound && (
            <div className="signal-detection-below-text">
              <Text strong>Originally:</Text>{' '}
              <Text>
                {selectReligiousTransparencyData?.metaphoricReligiousTermFound ===
                true
                  ? 'True'
                  : selectReligiousTransparencyData?.metaphoricReligiousTermFound ===
                      false
                    ? 'False'
                    : ''}
              </Text>
            </div>
          )}
      </div>
      <div className="signal-detection-form-item signal-detection-form-item">
        <Form.Item
          label={
            <span className="d-flex align-center">
              Metaphoric Religious Term List{' '}
              {infoIcon(metaphoricReligiousTermListPopoverContent)}
            </span>
          }
          name="religiousMetaphoricReligiousTermList"
          className={
            !isEmpty(feedbackData?.religiousMetaphoricReligiousTermList)
              ? 'signal-detection-form-item--with-below-text'
              : 'signal-detection-form-item'
          }
        >
          <CaseInsensitiveSelect
            mode="tags"
            size="middle"
            options={[]}
            placeholder="Enter metaphoric terms"
          />
        </Form.Item>
        {!(
          JSON.stringify(feedbackData?.religiousMetaphoricReligiousTermList) ===
          JSON.stringify(
            selectReligiousTransparencyData?.metaphoricReligiousTermList,
          )
        ) && (
          <div className="signal-detection-below-text">
            <span className="font-w-600">Originally:</span>{' '}
            <span>
              {(
                selectReligiousTransparencyData?.metaphoricReligiousTermList ||
                []
              )
                .map((val: string) => toTitleCase(val))
                .join(', ') || '--'}
            </span>
          </div>
        )}
      </div>
      <div className="signal-detection-form-item signal-detection-form-item">
        <Form.Item
          label={
            <span className="d-flex align-center">
              Disrespect Towards Religion{' '}
              {infoIcon(disrespectTowardsReligionPopoverContent)}
            </span>
          }
          name="religiousDisrespectTowardsReligion"
          className={
            isBoolean(feedbackData?.religiousDisrespectTowardsReligion)
              ? 'signal-detection-form-item--with-below-text'
              : 'signal-detection-form-item'
          }
        >
          <Select
            size="middle"
            options={[
              { label: 'True', value: true },
              { label: 'False', value: false },
            ]}
          />
        </Form.Item>
        {isBoolean(feedbackData?.religiousDisrespectTowardsReligion) &&
          isBoolean(
            selectReligiousTransparencyData?.disrespectTowardsReligion,
          ) &&
          feedbackData?.religiousDisrespectTowardsReligion !==
            selectReligiousTransparencyData?.disrespectTowardsReligion && (
            <div className="signal-detection-below-text">
              <Text strong>Originally:</Text>{' '}
              <Text>
                {selectReligiousTransparencyData?.disrespectTowardsReligion ===
                true
                  ? 'True'
                  : selectReligiousTransparencyData?.disrespectTowardsReligion ===
                      false
                    ? 'False'
                    : ''}
              </Text>
            </div>
          )}
      </div>
      <div className="signal-detection-form-item signal-detection-form-item">
        <Form.Item
          label={
            <span className="d-flex align-center">
              Disrespect Trigger List{' '}
              {infoIcon(disrespectTriggerListPopoverContent)}
            </span>
          }
          name="religiousDisrespectTriggerList"
          className={
            !isEmpty(feedbackData?.religiousDisrespectTriggerList)
              ? 'signal-detection-form-item--with-below-text'
              : 'signal-detection-form-item'
          }
        >
          <CaseInsensitiveSelect
            mode="tags"
            size="middle"
            options={[]}
            placeholder="Enter disrespect triggers"
          />
        </Form.Item>
        {!(
          JSON.stringify(feedbackData?.religiousDisrespectTriggerList) ===
          JSON.stringify(selectReligiousTransparencyData?.disrespectTriggerList)
        ) && (
          <div className="signal-detection-below-text">
            <span className="font-w-600">Originally:</span>{' '}
            <span>
              {(selectReligiousTransparencyData?.disrespectTriggerList || [])
                .map((val: string) => toTitleCase(val))
                .join(', ') || '--'}
            </span>
          </div>
        )}
      </div>
      <div className="signal-detection-form-item signal-detection-form-item">
        <Form.Item
          label={
            <span className="d-flex align-center">
              Disrespected Religions{' '}
              {infoIcon(disrespectedReligionsPopoverContent)}
            </span>
          }
          name="religiousDisrespectedReligions"
          className={
            !isEmpty(feedbackData?.religiousDisrespectedReligions)
              ? 'signal-detection-form-item--with-below-text'
              : 'signal-detection-form-item'
          }
        >
          <CaseInsensitiveSelect
            mode="tags"
            size="middle"
            options={[]}
            placeholder="Enter disrespected religions"
          />
        </Form.Item>
        {!(
          JSON.stringify(feedbackData?.religiousDisrespectedReligions) ===
          JSON.stringify(selectReligiousTransparencyData?.disrespectedReligions)
        ) && (
          <div className="signal-detection-below-text">
            <span className="font-w-600">Originally:</span>{' '}
            <span>
              {(selectReligiousTransparencyData?.disrespectedReligions || [])
                .map((val: string) => toTitleCase(val))
                .join(', ') || '--'}
            </span>
          </div>
        )}
      </div>
      <div className="signal-detection-form-item signal-detection-form-item">
        <Form.Item
          label={
            <span className="d-flex align-center">
              Comparison Frame Detected{' '}
              {infoIcon(comparisonFrameDetectedPopoverContent)}
            </span>
          }
          name="religiousComparisonFrameDetected"
          className={
            isBoolean(feedbackData?.religiousComparisonFrameDetected)
              ? 'signal-detection-form-item--with-below-text'
              : 'signal-detection-form-item'
          }
        >
          <Select
            size="middle"
            options={[
              { label: 'True', value: true },
              { label: 'False', value: false },
            ]}
          />
        </Form.Item>
        {isBoolean(feedbackData?.religiousComparisonFrameDetected) &&
          isBoolean(selectReligiousTransparencyData?.comparisonFrameDetected) &&
          feedbackData?.religiousComparisonFrameDetected !==
            selectReligiousTransparencyData?.comparisonFrameDetected && (
            <div className="signal-detection-below-text">
              <Text strong>Originally:</Text>{' '}
              <Text>
                {selectReligiousTransparencyData?.comparisonFrameDetected ===
                true
                  ? 'True'
                  : selectReligiousTransparencyData?.comparisonFrameDetected ===
                      false
                    ? 'False'
                    : ''}
              </Text>
            </div>
          )}
      </div>

      <div className="signal-detection-form-item signal-detection-form-item">
        <Form.Item
          label={
            <span className="d-flex align-center">
              Compared Religions {infoIcon(comparedReligionsPopoverContent)}
            </span>
          }
          name="religiousComparedReligions"
          className={
            !isEmpty(feedbackData?.religiousComparedReligions)
              ? 'signal-detection-form-item--with-below-text'
              : 'signal-detection-form-item'
          }
        >
          <CaseInsensitiveSelect
            mode="tags"
            size="middle"
            options={[]}
            placeholder="Enter compared religions"
          />
        </Form.Item>
        {!(
          JSON.stringify(feedbackData?.religiousComparedReligions) ===
          JSON.stringify(selectReligiousTransparencyData?.comparedReligions)
        ) && (
          <div className="signal-detection-below-text">
            <span className="font-w-600">Originally:</span>{' '}
            <span>
              {(selectReligiousTransparencyData?.comparedReligions || [])
                .map((val: string) => toTitleCase(val))
                .join(', ') || '--'}
            </span>
          </div>
        )}
      </div>
      <div className="signal-detection-form-item signal-detection-form-item">
        <Form.Item
          label={
            <span className="d-flex align-center">
              Doctrine Claim Type {infoIcon(doctrineClaimTypePopoverContent)}
            </span>
          }
          name="religiousDoctrineClaimType"
          className={
            !isEmpty(feedbackData?.religiousDoctrineClaimType)
              ? 'signal-detection-form-item--with-below-text'
              : 'signal-detection-form-item'
          }
        >
          <CaseInsensitiveSelect
            mode="tags"
            size="middle"
            options={[
              { label: 'None', value: 'none' },
              { label: 'Asserted', value: 'asserted' },
              { label: 'Challenged', value: 'challenged' },
              { label: 'Described', value: 'described' },
            ]}
            placeholder="Select claim type"
          />
        </Form.Item>
        {!(
          JSON.stringify(feedbackData?.religiousDoctrineClaimType) ===
          JSON.stringify(selectReligiousTransparencyData?.doctrineClaimType)
        ) && (
          <div className="signal-detection-below-text">
            <span className="font-w-600">Originally:</span>{' '}
            <span>
              {(selectReligiousTransparencyData?.doctrineClaimType || [])
                .map((val: string) => toTitleCase(val))
                .join(', ') || '--'}
            </span>
          </div>
        )}
      </div>
      <div className="signal-detection-form-item signal-detection-form-item">
        <Form.Item
          label={
            <span className="d-flex align-center">
              Framing Strength {infoIcon(framingStrengthPopoverContent)}
            </span>
          }
          name="religiousFramingStrength"
          className={
            !isEmpty(feedbackData?.religiousFramingStrength)
              ? 'signal-detection-form-item--with-below-text'
              : 'signal-detection-form-item'
          }
        >
          <CaseInsensitiveSelect
            mode="tags"
            size="middle"
            options={[
              { label: 'Weak', value: 'Weak' },
              { label: 'Moderate', value: 'Moderate' },
              { label: 'Strong', value: 'Strong' },
            ]}
            placeholder="Select framing strength"
          />
        </Form.Item>
        {!(
          JSON.stringify(feedbackData?.religiousFramingStrength) ===
          JSON.stringify(selectReligiousTransparencyData?.framingStrength)
        ) && (
          <div className="signal-detection-below-text">
            <span className="font-w-600">Originally:</span>{' '}
            <span>
              {(selectReligiousTransparencyData?.framingStrength || [])
                .map((val: string) => toTitleCase(val))
                .join(', ') || '--'}
            </span>
          </div>
        )}
      </div>
      <div className="signal-detection-form-item signal-detection-form-item">
        <Form.Item
          label={
            <span className="d-flex align-center">
              Draft Stance By Religion{' '}
              {infoIcon(draftStanceByReligionPopoverContent)}
            </span>
          }
          name="religiousDraftStanceByReligion"
          className={
            !isEmpty(feedbackData?.religiousDraftStanceByReligion)
              ? 'signal-detection-form-item--with-below-text'
              : 'signal-detection-form-item'
          }
        >
          <CaseInsensitiveSelect
            mode="tags"
            size="middle"
            options={[]}
            placeholder="Select stance(s) for each religion (e.g. christianity:neutral-1)"
          />
        </Form.Item>
        {!isEmpty(feedbackData?.religiousDraftStanceByReligion) &&
          !isEmpty(selectReligiousTransparencyData?.draftStanceByReligion) &&
          feedbackData?.religiousDraftStanceByReligion !==
            selectReligiousTransparencyData?.draftStanceByReligion && (
            <div className="signal-detection-below-text">
              <span className="font-w-600">Originally:</span>{' '}
              <span>
                {JSON.stringify(
                  selectReligiousTransparencyData?.draftStanceByReligion,
                )}
              </span>
            </div>
          )}
      </div>
    </Form>
  );
};

export default BiasDetectedForm;
